import { useDownloadLinks } from '@/hooks/useDownloadLinks';
import { mascotUrls } from '@/mocks/home';

const platforms = [
  {
    key: 'android' as const,
    name: 'Android',
    icon: 'ri-android-line',
    desc: '支持 Android 8.0 及以上系统',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    borderColor: 'border-green-500',
    shadowColor: 'shadow-[4px_4px_0_0_#86efac]',
    hoverShadow: 'hover:shadow-[4px_4px_0_0_#4ade80]',
  },
  {
    key: 'ios' as const,
    name: 'iOS',
    icon: 'ri-apple-line',
    desc: '支持 iOS 14.0 及以上系统',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-500',
    shadowColor: 'shadow-[4px_4px_0_0_#d1d5db]',
    hoverShadow: 'hover:shadow-[4px_4px_0_0_#9ca3af]',
  },
  {
    key: 'harmonyos' as const,
    name: 'HarmonyOS',
    icon: 'ri-smartphone-line',
    desc: '支持 HarmonyOS 2.0 及以上系统',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-500',
    shadowColor: 'shadow-[4px_4px_0_0_#fca5a5]',
    hoverShadow: 'hover:shadow-[4px_4px_0_0_#f87171]',
  },
  {
    key: 'windows' as const,
    name: 'Windows',
    icon: 'ri-windows-line',
    desc: '支持 Windows 10 及以上系统',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600',
    borderColor: 'border-pink-500',
    shadowColor: 'shadow-[4px_4px_0_0_#ff9db8]',
    hoverShadow: 'hover:shadow-[4px_4px_0_0_#ff6b8a]',
  },
];

export default function Download() {
  const { links, loading, error, reload } = useDownloadLinks();

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* 漫画网点背景 */}
        <div className="absolute inset-0 halftone-pink pointer-events-none" />
        <div className="absolute inset-0 line-grid pointer-events-none" />
        {/* 放射速度线 */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] pointer-events-none opacity-[0.15]">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 h-px w-full bg-gray-900 origin-left"
              style={{ transform: `translateX(50%) rotate(${i * 30}deg)` }}
            />
          ))}
        </div>

        <div className="relative px-4 md:px-8 lg:px-16 xl:px-24 text-center">
          <div className="relative max-w-4xl mx-auto">
            {/* 站娘方框 */}
            <div className="w-24 h-24 mx-auto mb-8 rounded-lg border-2 border-gray-900 bg-white shadow-[5px_5px_0_0_#ff9db8] overflow-hidden rotate-[-2deg]">
              <img
                src="/images/logo/logo.webp"
                alt="天天漫画"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-lg border-2 border-gray-900 bg-pink-50 shadow-[3px_3px_0_0_#ff9db8]">
              <i className="ri-sparkling-line text-pink-500" />
              <span className="text-xs tracking-[0.25em] font-medium text-gray-700 uppercase">
                官方正版
              </span>
            </div>

            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5">
              天天漫画下载
            </h1>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-4">
              天天漫画官方正版下载，支持 Android、iOS、HarmonyOS 和 Windows 四大平台。选择适合你的设备，立即开始漫画阅读之旅。
            </p>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              天天漫画APP下载安全无毒，所有版本均经过官方严格测试。下载完全免费，基础功能无需付费即可使用。
            </p>
            <img
              src={mascotUrls.download}
              alt="站娘"
              className="h-32 w-auto mx-auto mt-8 lg:hidden"
            />
          </div>
        </div>
      </section>

      {/* Platform Cards */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 pb-20">
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="text-center py-20">
              <div className="w-12 h-12 rounded-lg border-2 border-gray-900 bg-white border-t-pink-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">正在加载下载信息...</p>
            </div>
          )}

          {!loading && error && (
            <div className="mb-8 flex items-center justify-between gap-4 rounded-lg border-2 border-gray-900 bg-amber-50 shadow-[4px_4px_0_0_#fcd34d] px-6 py-4">
              <div className="flex items-center gap-3 text-sm text-amber-700">
                <i className="ri-error-warning-line text-lg" />
                <span>{error}</span>
              </div>
              <button
                onClick={reload}
                className="shrink-0 inline-flex items-center gap-2 text-sm font-medium text-gray-900 border-2 border-gray-900 rounded-lg px-4 py-2 bg-white shadow-[3px_3px_0_0_#fcd34d] hover:-translate-y-0.5 transition-all whitespace-nowrap"
              >
                <i className="ri-refresh-line" /> 重试
              </button>
            </div>
          )}

          {!loading && links && (
            <div className="grid md:grid-cols-2 gap-6">
              {platforms.map((platform) => {
                const info = links[platform.key];
                return (
                  <div
                    key={platform.key}
                    className="group relative bg-white rounded-lg border-2 border-gray-900 p-6 md:p-8 shadow-[5px_5px_0_0_#ff9db8] hover:shadow-[5px_5px_0_0_#1a1a1a] hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
                  >
                    {/* 网点角标 */}
                    <div className="absolute top-3 right-3 w-14 h-14 halftone-pink pointer-events-none" />

                    <div className="flex items-start gap-5">
                      <div
                        className={`w-16 h-16 rounded-lg border-2 border-gray-900 bg-white flex items-center justify-center flex-shrink-0 ${platform.shadowColor} ${platform.hoverShadow} group-hover:-translate-y-1 transition-all duration-300`}
                      >
                        <i className={`${platform.icon} text-3xl ${platform.textColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{platform.name}</h3>
                          <span className={`text-xs px-3 py-1 rounded-lg border border-gray-900 ${platform.bgColor} ${platform.textColor} font-bold`}>
                            {info.version}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">{platform.desc}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-400 mb-5">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line" /> {info.updated}
                          </span>
                        </div>

                        <a
                          href={info.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 bg-pink-500 border-2 border-gray-900 text-white font-bold rounded-lg shadow-[4px_4px_0_0_#1a1a1a] hover:bg-pink-600 hover:-translate-y-0.5 transition-all duration-200"
                        >
                          <i className="ri-download-2-line" />
                          立即下载 {platform.name} 版
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Note */}
          <div className="mt-12 bg-white rounded-lg border-2 border-gray-900 p-6 md:p-8 shadow-[5px_5px_0_0_#ff9db8]">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg border-2 border-gray-900 bg-pink-50 flex items-center justify-center flex-shrink-0 shadow-[3px_3px_0_0_#ff9db8]">
                <i className="ri-information-line text-pink-500 text-xl" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">下载须知</h4>
                <ul className="text-sm text-gray-500 space-y-1.5">
                  <li>· 请确保从正规渠道进行天天漫画官方正版下载，避免第三方来源带来的安全风险。</li>
                  <li>· 下载安装包后，部分系统可能需要手动授予安装权限。</li>
                  <li>· 如遇下载问题，请尝试切换网络或稍后重试。</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hidden keywords */}
      <div className="sr-only" aria-hidden="true">
        <p>天天漫画下载支持 Android 手机、iPhone、华为鸿蒙手机和 Windows 电脑。天天漫画APP下载完全免费，天天漫画官方正版下载提供安全可靠的安装包。如果你需要天天漫画下载，请认准本页面的官方下载入口。</p>
      </div>
    </div>
  );
}